import Logger from "../../../core/internal/logger"
import { InAppMessageDeliverResponse } from "./InAppMessageDeliverResponse"
import { InAppMessageDeliverRequest } from "./InAppMessageDeliverRequest"
import { InAppMessagePresentRequest } from "../present/InAppMessagePresentRequest"
import { InAppMessagePresentProcessor } from "../present/InAppMessagePresentProcessor"
import { UserManager } from "../../../core/internal/user/UserManager"
import WorkspaceFetcher from "../../../core/internal/workspace/WorkspaceFetcher"
import { InAppMessageIdentifierChecker } from "../evaluation/InAppMessageIdentifierChecker"
import { InAppMessageLayoutResolver } from "../evaluation/InAppMessageLayoutResolver"
import { InAppMessageEligibilityRequest } from "../../../core/internal/evaluation/evalautor/iam/eligibility/InAppMessageEligibilityRequest"
import { InAppMessageEvaluateProcessor } from "../evaluation/InAppMessageEvaluateProcessor"

export class InAppMessageDeliverProcessor {
  constructor(
    private readonly workspaceFetcher: WorkspaceFetcher,
    private readonly userManager: UserManager,
    private readonly identifierChecker: InAppMessageIdentifierChecker,
    private readonly layoutResolver: InAppMessageLayoutResolver,
    private readonly evaluateProcessor: InAppMessageEvaluateProcessor,
    private readonly presentProcessor: InAppMessagePresentProcessor
  ) {}

  private response(request: InAppMessageDeliverRequest): InAppMessageDeliverResponse {
    try {
      return this.deliver(request)
    } catch (err) {
      Logger.log.error(`Failed to process InAppMessageDeliver: ${err}`)
      return InAppMessageDeliverResponse.of(request, "EXCEPTION")
    }
  }

  process(request: InAppMessageDeliverRequest): InAppMessageDeliverResponse {
    Logger.log.debug(`InAppMessage Deliver Request: ${request}`)
    const response = this.response(request)
    Logger.log.debug(`InAppMessage Deliver Response: ${response}`)
    return response
  }

  private deliver(request: InAppMessageDeliverRequest): InAppMessageDeliverResponse {
    // check Workspace
    const workspace = this.workspaceFetcher.get()
    if (!workspace) {
      return InAppMessageDeliverResponse.of(request, "WORKSPACE_NOT_FOUND")
    }

    // check InAppMessage
    const inAppMessage = workspace.getInAppMessageOrNull(request.inAppMessageKey)
    if (!inAppMessage) {
      return InAppMessageDeliverResponse.of(request, "IN_APP_MESSAGE_NOT_FOUND")
    }

    // check User
    const user = this.userManager.resolve()
    const isIdentifierChanged = this.identifierChecker.isIdentifierChanged(request.identifiers, user.identifiers)
    if (isIdentifierChanged) {
      return InAppMessageDeliverResponse.of(request, "IDENTIFIER_CHANGED")
    }

    // resolve layout
    const layoutEvaluation = this.layoutResolver.resolve(workspace, inAppMessage, user)

    // check Evaluation (re-eval + dedup)
    const eligibilityRequest = new InAppMessageEligibilityRequest(workspace, user, inAppMessage, request.requestedAt)
    const eligibilityEvaluation = this.evaluateProcessor.process("DELIVER", eligibilityRequest)

    if (!eligibilityEvaluation.isEligible) {
      return InAppMessageDeliverResponse.of(request, "INELIGIBLE")
    }

    // present
    const presentRequest = InAppMessagePresentRequest.of(
      request,
      inAppMessage,
      user,
      eligibilityEvaluation,
      layoutEvaluation
    )
    const presentResponse = this.presentProcessor.process(presentRequest)

    return InAppMessageDeliverResponse.of(request, "PRESENT", presentResponse)
  }
}
